import React, { useContext, useEffect, useRef, useState } from "react";
import { CanvasContext } from "../../../contexts/CanvasContext";

const LinkComponent = ({ link }) => {
  const { setCurrentPage, pages, hoveredDivision, files } =
    useContext(CanvasContext);
  const linkRef = useRef(null);
  const isHovered = link.id === hoveredDivision;
  const [isDivHovered, setIsDivHovered] = useState(false);
  const [isDivFocused, setIsDivFocused] = useState(false);
  const [isDivActive, setIsDivActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = link;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = link;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = link;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = link;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const generateKeyframes = () => {
    if (!link.keyframes || link.keyframes.length === 0) return null;

    return link.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };

  useEffect(() => {
    if (linkRef.current && link.keyframes && link.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = linkRef.current.animate(keyframes, {
        duration: parseFloat(link.animationDuration) * 1000 || 1000,
        delay: parseFloat(link.animationDelay) * 1000 || 0,
        iterations:
          link.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(link.animationIterationCount) || 1,
        direction: link.animationDirection || "normal",
        fill: link.animationFillMode || "none",
        easing: link.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    link.keyframes,
    link.animationDuration,
    link.animationDelay,
    link.animationIterationCount,
    link.animationDirection,
    link.animationFillMode,
    link.animationTimingFunction,
  ]);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (link.backgroundImage && link.backgroundImage.startsWith("./")) {
        const imageName = link.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(link.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [link.backgroundImage, files]);

  const focusedStyles =
    isDivFocused && link.pseudoClass === "focus"
      ? {
          width: link.focusedwidth || link.width || "auto",
          height: link.focusedheight || link.height || "auto",

          padding: link.focusedpadding || link.padding || "0",
          paddingTop: link.focusedpaddingTop || link.focusedpadding || "0",
          paddingRight: link.focusedpaddingRight || link.focusedpadding || "0",
          paddingBottom:
            link.focusedpaddingBottom || link.focusedpadding || "0",
          paddingLeft: link.focusedpaddingLeft || link.focusedpadding || "0",
          margin: link.focusedmargin || link.margin || "0px",
          marginTop: link.focusedmarginTop || link.focusedmargin || "0px",
          marginRight: link.focusedmarginRight || link.focusedmargin || "0px",
          marginBottom: link.focusedmarginBottom || link.focusedmargin || "0px",
          marginLeft: link.focusedmarginLeft || link.focusedmargin || "0px",

          borderTop:
            link.focusedborderTop ||
            link.focusedborder ||
            link.border ||
            "none",
          borderRight:
            link.focusedborderRight ||
            link.focusedborder ||
            link.border ||
            "none",
          borderBottom:
            link.focusedborderBottom ||
            link.focusedborder ||
            link.border ||
            "none",
          borderLeft:
            link.focusedborderLeft ||
            link.focusedborder ||
            link.border ||
            "none",
          borderSapcing:
            link.focusedborderSpacing || link.borderSpacing || "0px",
          borderCollapse:
            link.focusedborderCollapse || link.borderCollapse || "0px",
          borderRadius: link.focusedborderRadius || link.borderRadius || "0",
          outline: link.focusedoutline || link.outline || "none",

          position: link.focusedposition || link.position || "relative",
          top: link.focusedtop || link.top || "auto",
          right: link.focusedright || link.right || "auto",
          bottom: link.focusedbottom || link.bottom || "auto",
          left: link.focusedleft || link.left || "auto",
          zIndex: link.focusedzIndex || link.zIndex || "auto",
          boxShadow: link.focusedboxShadow || link.boxShadow || "none",
          opacity: link.focusedOpacity || link.opacity || 1,

          color: link.focusedcolor || link.color || "#000",
          backgroundColor:
            link.focusedbackgroundColor || link.backgroundColor || "white",
          backgroundImage: (() => {
            if (link.focusedbackgroundImage) {
              if (link.focusedbackgroundImage.startsWith("./")) {
                const imageName = link.focusedbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${link.focusedbackgroundImage})`;
            }
            if (link.backgroundImage) {
              if (link.backgroundImage.startsWith("./")) {
                const imageName = link.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${link.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            link.focusedbackgroundPosition ||
            link.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            link.focusedbackgroundSize || link.backgroundSize || "auto",
          backgroundRepeat:
            link.focusedbackgroundRepeat || link.backgroundRepeat || "repeat",
          backgroundAttachment:
            link.focusedbackgroundAttachment ||
            link.backgroundAttachment ||
            "scroll",

          display: link.focuseddisplay || link.display || "block",

          fontFamily: link.focusedfontFamily || link.fontFamily || "inherit",
          fontSize: link.focusedfontSize || link.fontSize || "inherit",
          fontWeight: link.focusedfontWeight || link.fontWeight || "normal",
          fontStyle: link.focusedfontStyle || link.fontStyle || "normal",
          fontVariant: link.focusedfontVariant || link.fontVariant || "normal",
          lineHeight: link.focusedlineHeight || link.lineHeight || "normal",
          textShadow: link.focusedtextShadow || link.textShadow || "none",
          textAlign: link.focusedtextAlign || link.textAlign || "left",
          verticalAlign:
            link.focusedverticalAlign || link.verticalAlign || "baseline",
          textDecoration:
            link.focusedtextDecoration || link.textDecoration || "none",
          textTransform:
            link.focusedtextTransform || link.textTransform || "none",
          letterSpacing:
            link.focusedletterSpacing || link.letterSpacing || "normal",
          wordSpacing: link.focusedwordSpacing || link.wordSpacing || "normal",
          textIndent: link.focusedtextIndent || link.textIndent || "0",
          whiteSpace: link.focusedwhiteSpace || link.whiteSpace || "normal",
          textOverflow: link.focusedtextOverflow || link.textOverflow || "clip",
          direction: link.focuseddirection || link.direction || "ltr",
          unicodeBidi: link.focusedunicodeBidi || link.unicodeBidi || "normal",
          writingMode:
            link.focusedwritingMode || link.writingMode || "horizontal-tb",

          cursor: link.focusedcursor || link.cursor || "default",
          visibility: link.focusedvisibility || "visible",
          filter: link.focusedfilter || link.filter || "none",
          backdropFilter:
            link.focusedbackdropFilter || link.backdropFilter || "none",
          mixBlendMode:
            link.focusedmixBlendMode || link.mixBlendMode || "normal",

          transform: link.focusedtransform || link.transform || "none",
          transition: link.focusedtransition || link.transition || "none",
        }
      : {};

  const hoverStyles =
    isDivHovered && link.pseudoClass === "hover"
      ? {
          width: link.hoveredwidth || link.width || "100px",
          height: link.hoveredheight || link.height || "auto",

          padding: link.hoveredpadding || link.padding || "0",
          paddingTop: link.hoveredpaddingTop || link.hoveredpadding || "0",
          paddingRight: link.hoveredpaddingRight || link.hoveredpadding || "0",
          paddingBottom:
            link.hoveredpaddingBottom || link.hoveredpadding || "0",
          paddingLeft: link.hoveredpaddingLeft || link.hoveredpadding || "0",
          margin: link.hoveredmargin || link.margin || "0px",
          marginTop: link.hoveredmarginTop || link.hoveredmargin || "0px",
          marginRight: link.hoveredmarginRight || link.hoveredmargin || "5px",
          marginBottom: link.hoveredmarginBottom || link.hoveredmargin || "0px",
          marginLeft: link.hoveredmarginLeft || link.hoveredmargin || "0px",

          borderTop:
            link.hoveredborderTop ||
            link.hoveredborder ||
            link.border ||
            "1px solid #000000",
          borderRight:
            link.hoveredborderRight ||
            link.hoveredborder ||
            link.border ||
            "1px solid #000000",
          borderBottom:
            link.hoveredborderBottom ||
            link.hoveredborder ||
            link.border ||
            "1px solid #000000",
          borderLeft:
            link.hoveredborderLeft ||
            link.hoveredborder ||
            link.border ||
            "1px solid #000000",
          borderSapcing:
            link.hoveredborderSpacing || link.borderSpacing || "0px",
          borderCollapse:
            link.hoveredborderCollapse || link.borderCollapse || "0px",
          borderRadius: link.hoveredborderRadius || link.borderRadius || "0",
          outline: link.hoveredoutline || link.outline || "none",

          position: link.hoveredposition || link.position || "relative",
          top: link.hoveredtop || link.top || "auto",
          right: link.hoveredright || link.right || "auto",
          bottom: link.hoveredbottom || link.bottom || "auto",
          left: link.hoveredleft || link.left || "auto",
          zIndex: link.hoveredzIndex || link.zIndex || "auto",
          boxShadow: link.hoveredboxShadow || link.boxShadow || "none",
          opacity: link.hoveredOpacity || link.opacity || 1,

          color: link.hoveredcolor || link.color || "#000000",
          backgroundColor:
            link.hoveredbackgroundColor || link.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (link.hoveredbackgroundImage) {
              if (link.hoveredbackgroundImage.startsWith("./")) {
                const imageName = link.hoveredbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${link.hoveredbackgroundImage})`;
            }
            if (link.backgroundImage) {
              if (link.backgroundImage.startsWith("./")) {
                const imageName = link.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${link.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            link.hoveredbackgroundPosition ||
            link.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            link.hoveredbackgroundSize || link.backgroundSize || "auto",
          backgroundRepeat:
            link.hoveredbackgroundRepeat || link.backgroundRepeat || "repeat",
          backgroundAttachment:
            link.hoveredbackgroundAttachment ||
            link.backgroundAttachment ||
            "scroll",

          display: link.hovereddisplay || link.display || "block",

          fontFamily: link.hoveredfontFamily || link.fontFamily || "inherit",
          fontSize: link.hoveredfontSize || link.fontSize || "inherit",
          fontWeight: link.hoveredfontWeight || link.fontWeight || "normal",
          fontStyle: link.hoveredfontStyle || link.fontStyle || "normal",
          fontVariant: link.hoveredfontVariant || link.fontVariant || "normal",
          lineHeight: link.hoveredlineHeight || link.lineHeight || "normal",
          textShadow: link.hoveredtextShadow || link.textShadow || "none",
          textAlign: link.hoveredtextAlign || link.textAlign || "left",
          verticalAlign:
            link.hoveredverticalAlign || link.verticalAlign || "baseline",
          textDecoration:
            link.hoveredtextDecoration || link.textDecoration || "none",
          textTransform:
            link.hoveredtextTransform || link.textTransform || "none",
          letterSpacing:
            link.hoveredletterSpacing || link.letterSpacing || "normal",
          wordSpacing: link.hoveredwordSpacing || link.wordSpacing || "normal",
          textIndent: link.hoveredtextIndent || link.textIndent || "0",
          whiteSpace: link.hoveredwhiteSpace || link.whiteSpace || "normal",
          textOverflow: link.hoveredtextOverflow || link.textOverflow || "clip",
          direction: link.hovereddirection || link.direction || "ltr",
          unicodeBidi: link.hoveredunicodeBidi || link.unicodeBidi || "normal",
          writingMode:
            link.hoveredwritingMode || link.writingMode || "horizontal-tb",

          cursor: link.hoveredcursor || link.cursor || "default",
          visibility: link.hoveredvisibility || "visible",
          filter: link.hoveredfilter || link.filter || "none",
          backdropFilter:
            link.hoveredbackdropFilter || link.backdropFilter || "none",
          mixBlendMode:
            link.hoveredmixBlendMode || link.mixBlendMode || "normal",

          transform: link.hoveredtransform || link.transform || "none",
          transition: link.hoveredtransition || link.transition || "none",
        }
      : {};

  const activeStyles =
    isDivActive && link.pseudoClass === "active"
      ? {
          width: link.activatedwidth || link.width || "100px",
          height: link.hoveredheight || link.height || "auto",

          padding: link.activatedpadding || link.padding || "0",
          paddingTop: link.activatedpaddingTop || link.activatedpadding || "0",
          paddingRight:
            link.activatedpaddingRight || link.activatedpadding || "0",
          paddingBottom:
            link.activatedpaddingBottom || link.activatedpadding || "0",
          paddingLeft:
            link.activatedpaddingLeft || link.activatedpadding || "0",
          margin: link.activatedmargin || link.margin || "0px",
          marginTop: link.activatedmarginTop || link.activatedmargin || "0px",
          marginRight:
            link.activatedmarginRight || link.activatedmargin || "5px",
          marginBottom:
            link.activatedmarginBottom || link.activatedmargin || "0px",
          marginLeft: link.activatedmarginLeft || link.activatedmargin || "0px",

          borderTop:
            link.activatedborderTop ||
            link.activatedborder ||
            link.border ||
            "1px solid #000000",
          borderRight:
            link.activatedborderRight ||
            link.activatedborder ||
            link.border ||
            "1px solid #000000",
          borderBottom:
            link.activatedborderBottom ||
            link.activatedborder ||
            link.border ||
            "1px solid #000000",
          borderLeft:
            link.activatedborderLeft ||
            link.activatedborder ||
            link.border ||
            "1px solid #000000",
          borderSapcing:
            link.activatedborderSpacing || link.borderSpacing || "0px",
          borderCollapse:
            link.activatedborderCollapse || link.borderCollapse || "0px",
          borderRadius: link.activatedborderRadius || link.borderRadius || "0",
          outline: link.activatedoutline || link.outline || "none",

          position: link.activatedposition || link.position || "relative",
          top: link.activatedtop || link.top || "auto",
          right: link.activatedright || link.right || "auto",
          bottom: link.activatedbottom || link.bottom || "auto",
          left: link.activatedleft || link.left || "auto",
          zIndex: link.activatedzIndex || link.zIndex || "auto",
          boxShadow: link.activatedboxShadow || link.boxShadow || "none",
          opacity: link.activatedOpacity || link.opacity || 1,
          color: link.activatedcolor || link.color || "#000000",
          backgroundColor:
            link.activatedbackgroundColor || link.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (link.activatedbackgroundImage) {
              if (link.activatedbackgroundImage.startsWith("./")) {
                const imageName = link.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${link.activatedbackgroundImage})`;
            }
            if (link.backgroundImage) {
              if (link.backgroundImage.startsWith("./")) {
                const imageName = link.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${link.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            link.activatedbackgroundPosition ||
            link.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            link.activatedbackgroundSize || link.backgroundSize || "auto",
          backgroundRepeat:
            link.activatedbackgroundRepeat || link.backgroundRepeat || "repeat",
          backgroundAttachment:
            link.activatedbackgroundAttachment ||
            link.backgroundAttachment ||
            "scroll",

          display: link.activateddisplay || link.display || "block",

          fontFamily: link.activatedfontFamily || link.fontFamily || "inherit",
          fontSize: link.activatedfontSize || link.fontSize || "inherit",
          fontWeight: link.activatedfontWeight || link.fontWeight || "normal",
          fontStyle: link.activatedfontStyle || link.fontStyle || "normal",
          fontVariant:
            link.activatedfontVariant || link.fontVariant || "normal",
          lineHeight: link.activatedlineHeight || link.lineHeight || "normal",
          textShadow: link.activatedtextShadow || link.textShadow || "none",
          textAlign: link.activatedtextAlign || link.textAlign || "left",
          verticalAlign:
            link.activatedverticalAlign || link.verticalAlign || "baseline",
          textDecoration:
            link.activatedtextDecoration || link.textDecoration || "none",
          textTransform:
            link.activatedtextTransform || link.textTransform || "none",
          letterSpacing:
            link.activatedletterSpacing || link.letterSpacing || "normal",
          wordSpacing:
            link.activatedwordSpacing || link.wordSpacing || "normal",
          textIndent: link.activatedtextIndent || link.textIndent || "0",
          whiteSpace: link.activatedwhiteSpace || link.whiteSpace || "normal",
          textOverflow:
            link.activatedtextOverflow || link.textOverflow || "clip",
          direction: link.activateddirection || link.direction || "ltr",
          unicodeBidi:
            link.activatedunicodeBidi || link.unicodeBidi || "normal",
          writingMode:
            link.activatedwritingMode || link.writingMode || "horizontal-tb",

          cursor: link.activatedcursor || link.cursor || "default",
          visibility: link.activatedvisibility || "visible",

          filter: link.activatedfilter || link.filter || "none",
          backdropFilter:
            link.activatedbackdropFilter || link.backdropFilter || "none",
          mixBlendMode:
            link.activatedmixBlendMode || link.mixBlendMode || "normal",

          transform: link.activatedtransform || link.transform || "none",
          transition: link.activatedtransition || link.transition || "none",
        }
      : {};

  const linkStyle = {
    width: link.width || "auto",
    height: link.height || "auto",
    margin: link.margin || "0px",
    marginTop: link.marginTop || link.margin || "0",
    marginRight: link.marginRight || link.margin || "0px",
    marginBottom: link.marginBottom || link.margin || "0",
    marginLeft: link.marginLeft || link.margin || "0",
    padding: link.padding || "0px",
    paddingTop: link.paddingTop || link.padding || "0",
    paddingRight: link.paddingRight || link.padding || "0",
    paddingBottom: link.paddingBottom || link.padding || "0",
    paddingLeft: link.paddingLeft || link.padding || "0",

    borderTop: isHovered
      ? "1px dashed #555"
      : link.borderTop || link.border || "none",
    borderRight: isHovered
      ? "1px dashed #555"
      : link.borderRight || link.border || "none",
    borderBottom: isHovered
      ? "1px dashed #555"
      : link.borderBottom || link.border || "none",
    borderLeft: isHovered
      ? "1px dashed #555"
      : link.borderLeft || link.border || "none",

    borderSapcing: link.borderSpacing || "0px",
    borderCollapse: link.borderCollapse || "0px",
    borderRadius: link.borderRadius || "0",
    outline: link.outline || "none",

    position: link.position || "static",
    top: link.top || "auto",
    right: link.right || "auto",
    bottom: link.bottom || "auto",
    left: link.left || "auto",
    zIndex: link.zIndex || "auto",

    backgroundColor: link.backgroundColor || "#ffffff",
    color: link.color || "#0000EE",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),
    backgroundSize: link.backgroundSize || "auto",
    backgroundPosition: link.backgroundPosition || "0% 0%",
    backgroundRepeat: link.backgroundRepeat || "repeat",
    backgroundAttachment: link.backgroundAttachment || "scroll",

    display: link.display || "block",

    boxShadow: link.boxShadow || "none",
    opacity: link.opacity || "1",
    overflow: link.overflow || "visible",
    overflowX: link.overflowX || "hidden",
    overflowY: link.overflowY || "hidden",

    fontSize: link.fontSize || "inherit",
    fontFamily: link.fontFamily || "inherit",
    fontWeight: link.fontWeight || "normal",
    fontStyle: link.fontStyle || "normal",
    fontVariant: link.fontVariant || "normal",
    lineHeight: link.lineHeight || "normal",
    textAlign: link.textAlign || "left",
    textDecoration: link.textDecoration || "underline",
    textTransform: link.textTransform || "none",
    textShadow: link.textShadow || "none",
    verticalAlign: link.verticalAlign || "baseline",
    letterSpacing: link.letterSpacing || "normal",
    wordSpacing: link.wordSpacing || "normal",
    textIndent: link.textIndent || "0",
    whiteSpace: link.whiteSpace || "normal",
    textOverflow: link.textOverflow || "clip",
    direction: link.direction || "ltr",
    unicodeBidi: link.unicodeBidi || "normal",
    writingMode: link.writingMode || "horizontal-tb",

    boxSizing: link.boxSizing || "border-box",
    visibility: link.visibility || "visible",
    userSelect: link.userSelect || "auto",
    cursor: link.cursor || "auto",

    filter: link.filter || "none",
    backdropFilter: link.backdropFilter || "none",
    mixBlendMode: link.mixBlendMode || "normal",
    transform: link.transform || "none",
    transition: link.transition || "none",

    scale: isHovered ? "1.01" : "1",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  const handleClick = (e) => {
    if (link.redirectTo) {
      e.preventDefault();
      const targetPage = pages.find((page) => page.name === link.redirectTo);
      if (targetPage) {
        setCurrentPage(targetPage);
      } else {
        console.error(`Page "${link.redirectTo}" not found`);
      }
    }
  };

  return (
    <a
      ref={linkRef}
      href={link.redirectTo ? `#${link.redirectTo}` : link.href || "#"}
      style={linkStyle}
      target={link.target || "_self"}
      rel={link.rel || ""}
      onClick={handleClick}
      onMouseEnter={() => setIsDivHovered(true)}
      onMouseLeave={() => setIsDivHovered(false)}
      onFocus={() => setIsDivFocused(true)}
      onBlur={() => setIsDivFocused(false)}
      onMouseDown={() => setIsDivActive(true)}
      onMouseUp={() => setIsDivActive(false)}
      tabIndex={0}
    >
      {link.text || "Link"}
    </a>
  );
};

export default LinkComponent;
